/** Shared by lab and med */

import { Logger } from "@/lib/logger/Logger";
import { supabase, Tables } from "@/lib/supabase";
import { StateCreator } from "zustand";
import { FormsStoreUnion } from ".";
import { produce } from "immer";
import { ExtendedFormTemplate } from "@/dentlab/src/pages/form-builder/types/formTypes";
import {
	SYSTEM_STANDARD_FORM,
	SYSTEM_STANDARD_FORM_ID,
} from "@/dentconnect/src/pages/job-request/constants/system-standard-form";

interface FormsState {
	formsByOrgId: Record<string, Record<string, ExtendedFormTemplate>>;
}

export const initialFormsState: FormsState = {
	formsByOrgId: {},
};

export interface FormsStoreSlice extends FormsState {
	fetchForms: (orgIds: string[]) => Promise<void>;
	fetchFormById: (formId: string) => Promise<ExtendedFormTemplate | null>;
}

export const createFormsStore: StateCreator<
	FormsStoreUnion,
	[],
	[],
	FormsStoreSlice
> = (set, get) => ({
	...initialFormsState,
	fetchForms: async (orgIds: string[]) => {
		const { data, error } = await supabase
			.from("forms")
			.select("*")
			.in("organization_id", orgIds);

		if (error) {
			Logger.error(error.message);
			return;
		}

		Logger.info("forms.store.fetchForms", { data, orgIds });

		set(
			produce((state) => {
				for (const orgId of orgIds) {
					state.formsByOrgId[orgId] = {};
				}
				if (data) {
					for (const form of data) {
						if (!state.formsByOrgId[form.organization_id]) {
							state.formsByOrgId[form.organization_id] = {};
						}
						state.formsByOrgId[form.organization_id][form.id] =
							form as unknown as ExtendedFormTemplate;
					}
				}
			})
		);
	},
	fetchFormById: async (formId: string) => {
		if (formId === SYSTEM_STANDARD_FORM_ID) {
			set(
				produce((state) => {
					if (
						!state.formsByOrgId[
							SYSTEM_STANDARD_FORM.organization_id
						]
					) {
						state.formsByOrgId[
							SYSTEM_STANDARD_FORM.organization_id
						] = {};
					}
					state.formsByOrgId[SYSTEM_STANDARD_FORM.organization_id][
						formId
					] = SYSTEM_STANDARD_FORM;
				})
			);
			return SYSTEM_STANDARD_FORM;
		}

		const existingForm = Object.values(get().formsByOrgId)
			.flatMap((orgForms) => Object.values(orgForms))
			.find((form) => form.id === formId);

		if (existingForm) {
			return existingForm;
		}

		// Fetch from database
		const { data, error } = await supabase
			.from("forms")
			.select("*")
			.eq("id", formId)
			.single();

		if (error) {
			Logger.error("forms.store.fetchFormById error", {}, error);
			return null;
		}

		if (!data) {
			return null;
		}

		const formTemplate = data as unknown as ExtendedFormTemplate;

		// Store in formsByOrgId
		set(
			produce((state) => {
				if (!state.formsByOrgId[data.organization_id]) {
					state.formsByOrgId[data.organization_id] = {};
				}
				state.formsByOrgId[data.organization_id][formId] = formTemplate;
			})
		);

		Logger.info("forms.store.fetchFormById success", {
			formId,
			formTemplate,
		});

		return formTemplate;
	},
});
